import { useEffect, useState } from 'react';

import { MediaQuery, MediaType } from '@/helpers/enums';
import { useShallowCallback, useShallowMemo } from '@/hooks/common';

export function useMedia<T>(queries: string[], values: T[], defaultValue: T) {
  const mediaQueryLists = useShallowMemo(
    () =>
      queries.map((q) =>
        typeof window !== 'undefined' ? window.matchMedia(q) : undefined
      ),
    [queries]
  );

  const getValue = useShallowCallback(() => {
    const index = mediaQueryLists.findIndex((mql) => mql && mql.matches);
    return typeof values[index] !== 'undefined' ? values[index] : defaultValue;
  }, [mediaQueryLists, values, defaultValue]);

  const [value, setValue] = useState(getValue);

  useEffect(() => {
    const handler = () => setValue(getValue);
    mediaQueryLists.forEach((mql) => {
      if (!mql) return;

      mql.addEventListener('change', handler);
    });

    return () =>
      mediaQueryLists.forEach((mql) => {
        if (!mql) return;

        mql.removeEventListener('change', handler);
      });
  }, [getValue, mediaQueryLists]);

  return value;
}

const queries = [
  MediaQuery.DESKTOP_L,
  MediaQuery.DESKTOP,
  MediaQuery.TABLET,
  MediaQuery.MOBILE,
  MediaQuery.MOBILE_S,
];
const types = [
  MediaType.DESKTOP_L,
  MediaType.DESKTOP,
  MediaType.TABLET,
  MediaType.MOBILE,
  MediaType.MOBILE_S,
];

export function useCurrentMediaType() {
  return typeof window !== 'undefined'
    ? useMedia(queries, types, undefined)
    : undefined;
}
